import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Check both possible token keys
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const errorMessage = error.response?.data?.message || '';
      if (!errorMessage.includes('MFA') &&
          !errorMessage.includes('verify') &&
          !window.location.pathname.includes('/mfa-verification')) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
        localStorage.removeItem('token');
if (window.location.pathname !== '/login') window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const sectionsAPI = {
  // Education CRUD operations
  education: {
    getAll: async () => {
      const response = await api.get('/api/education/');
      return { data: response.data };
    },
    add: async (educationData) => {
      const response = await api.post('/api/education/add', educationData);
      return { data: response.data };
    },
    update: async (id, educationData) => {
      const response = await api.put(`/api/education/update/${id}`, educationData);
      return { data: response.data };
    },
    delete: async (ids) => {
      const response = await api.delete('/api/education/delete/', { 
        data: { ids } 
      });
      return { data: response.data };
    }
  },

  // Certification CRUD operations
  certification: {
    getAll: async () => {
      const response = await api.get('/api/certification/');
      return { data: response.data };
    },
    add: async (certificationData) => {
      const response = await api.post('/api/certification/add', certificationData);
      return { data: response.data };
    },
    update: async (id, certificationData) => {
      const response = await api.put(`/api/certification/update/${id}`, certificationData);
      return { data: response.data };
    },
    delete: async (ids) => {
      const response = await api.delete('/api/certification/delete', { 
        data: { ids } 
      });
      return { data: response.data };
    }
  },


// Add this to your existing sectionsAPI object
employment: {
  getAll: async () => {
    const response = await api.get('/api/employment/');
    return { data: response.data };
  },
  add: async (employmentData) => {
    const response = await api.post('/api/employment/add', employmentData);
    return { data: response.data };
  },
  update: async (id, employmentData) => {
    const response = await api.put(`/api/employment/update/${id}`, employmentData);
    return { data: response.data };
  },
  delete: async (ids) => {
    const response = await api.delete('/api/employment/delete', {
      data: { ids }
    });
    return { data: response.data };
  }
},

reference: {
  getAll: async (employmentId) => api.get(`/api/references/${employmentId}`).then(r => ({ data: r.data })),
  add: async (employmentId, referenceData) => api.post(`/api/references/${employmentId}`, referenceData).then(r => ({ data: r.data })),
  resend: async (referenceId) => api.post(`/api/references/${referenceId}/resend`).then(r => ({ data: r.data })),

  // Public reference flow (token-based for non-portal users)
  getPrompts: async (referenceId, token) => {
    const params = token ? { token } : {};
    const response = await api.get(`/api/references/${referenceId}/prompts`, { params });
    return { data: response.data };
  },
  submitPrompts: async (referenceId, body) => {
    const response = await api.post(`/api/references/${referenceId}/prompts`, body);
    return { data: response.data };
  },
  submitFeedback: async (referenceId, body) => {
    const response = await api.post(`/api/references/${referenceId}/feedback`, body);
    return { data: response.data };
  },
  acknowledgeLegacy: async (referenceId, body) => {
    const response = await api.post(`/api/references/${referenceId}/acknowledge`, body);
    return { data: response.data };
  },

  // Authenticated views
  getResponses: async (employmentId) => {
    const response = await api.get(`/api/references/${employmentId}/responses`);
    return { data: response.data };
  },
  getAuditTrail: async (referenceId) => {
    const response = await api.get(`/api/references/${referenceId}/audit-trail`);
    return { data: response.data };
  },
  getMyResponse: async (referenceId) => {
    const response = await api.get(`/api/references/${referenceId}/my-response`);
    return { data: response.data };
  },
},

team: {
  getAll: async (employmentId) => api.get(`/api/teamroutes/${employmentId}/teams`).then(r => ({ data: r.data })),
  create: async (employmentId, teamData) => api.post(`/api/teamroutes/${employmentId}/teams`, teamData).then(r => ({ data: r.data })),
  addMember: async (employmentId, teamId, memberData) =>
    api.post(`/api/teamroutes/${employmentId}/teams/${teamId}/members`, memberData).then(r => ({ data: r.data })),

  getById: async (employmentId, teamId) => {
    const response = await api.get(`/api/teamroutes/${employmentId}/teams/${teamId}`);
    return { data: response.data };
  },
  update: async (employmentId, teamId, teamData) => {
    const response = await api.put(`/api/teamroutes/${employmentId}/teams/${teamId}`, teamData);
    return { data: response.data };
  },
  remove: async (employmentId, teamId) => {
    const response = await api.delete(`/api/teamroutes/${employmentId}/teams/${teamId}`);
    return { data: response.data };
  },

  listMembers: async (employmentId, teamId) => {
    const response = await api.get(`/api/teamroutes/${employmentId}/teams/${teamId}/members`);
    return { data: response.data };
  },
  updateMember: async (employmentId, teamId, memberId, memberData) => {
    const response = await api.put(`/api/teamroutes/${employmentId}/teams/${teamId}/members/${memberId}`, memberData);
    return { data: response.data };
  },
  deleteMember: async (employmentId, teamId, memberId) => {
    const response = await api.delete(`/api/teamroutes/${employmentId}/teams/${teamId}/members/${memberId}`);
    return { data: response.data };
  },
  getCandidateStructure: async (profileId) => {
  const response = await api.get(`/api/teamroutes/profile/${profileId}/structure`);
  return { data: response.data };
},

},

questionnaire: {
  getDefault: async () => api.get('/api/questionnaires/default').then(r => ({ data: r.data })),

  getAll: async () => {
    const response = await api.get('/api/questionnaires/');
    return { data: response.data };
  },
  getById: async (templateId) => {
    const response = await api.get(`/api/questionnaires/${templateId}`);
    return { data: response.data };
  },
  create: async (templateData) => {
    const response = await api.post('/api/questionnaires/', templateData);
    return { data: response.data };
  },
  update: async (templateId, templateData) => {
    const response = await api.put(`/api/questionnaires/${templateId}`, templateData);
    return { data: response.data };
  },
  remove: async (templateId) => {
    const response = await api.delete(`/api/questionnaires/${templateId}`);
    return { data: response.data };
  },
  seedDefaults: async () => {
    const response = await api.post('/api/questionnaires/admin/seed');
    return { data: response.data };
  },
},


notifications: {
    // GET all notifications (with optional onlyUnread filter)
    getAll: async (onlyUnread = false) => {
      const params = onlyUnread ? { onlyUnread: 'true' } : {};
      const response = await api.get('/api/notifications/', { params });
      return { data: response.data };
    },

    // PATCH mark single notification as read
    markAsRead: async (notificationId) => {
      const response = await api.patch(`/api/notifications/${notificationId}/read`);
      return { data: response.data };
    },

    // PATCH mark all notifications as read
    markAllAsRead: async () => {
      const response = await api.patch('/api/notifications/read-all');
      return { data: response.data };
    }
  }
};